import React, { useState, useEffect, useCallback, useRef } from 'react';
import { serverAPI } from '../../../services/api';
import { toast } from 'react-hot-toast';

interface AppInfo {
  name: string;
  display_name: string;
  status: 'running' | 'stopped' | 'unknown';
  version?: string;
  pids?: number[];
  memory_mb?: number;
  cpu_percent?: number;
  uptime?: string;
  config_files?: string[];
  log_files?: string[];
}

interface Props {
  serverId: number;
  wsConnected?: boolean;
}

const statusColor = (status: string) => {
  switch (status) {
    case 'running': return '#22c55e';
    case 'stopped': return '#ef4444';
    default:        return '#9ca3af';
  }
};

// Only show apps that are actually installed/detected
const isInstalled = (app: AppInfo) =>
  app.status === 'running' ||
  (app.pids && app.pids.length > 0) ||
  (app.version && app.version.trim().length > 0);

const AppStatusPanel: React.FC<Props> = ({ serverId, wsConnected }) => {
  const [apps, setApps]         = useState<AppInfo[]>([]);
  const [loading, setLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [logsModal, setLogsModal] = useState<{ open: boolean; app: string; content: string } | null>(null);
  const [logSearch, setLogSearch] = useState('');
  const [configModal, setConfigModal] = useState<{ open: boolean; app: string; content: string; configFile?: string; type?: string; phpConfig?: Record<string, string> } | null>(null);
  const [configSaving, setConfigSaving] = useState(false);
  const [restarting, setRestarting] = useState<string | null>(null);
  const cacheKey = `cloudayn_apps_${serverId}`;

  const loadApps = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await serverAPI.getApps(serverId);
      const all: AppInfo[] = res.data?.apps || [];
      const installed = all.filter(isInstalled);
      setApps(installed);
      // Cache installed apps
      try { localStorage.setItem(cacheKey, JSON.stringify(installed)); } catch {}
    } catch {
      // silent — keep stale data
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [serverId, cacheKey]);

  useEffect(() => {
    // Load from cache immediately
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        setApps(JSON.parse(cached));
        setLoading(false);
        // Refresh in background
        loadApps(true);
        return;
      }
    } catch {}
    loadApps(false);
  }, [loadApps, cacheKey]);

  const handleRestart = async (app: AppInfo) => {
    if (!window.confirm(`Restart ${app.display_name}?`)) return;
    setRestarting(app.name);
    try {
      await serverAPI.restartApp(serverId, app.name);
      toast.success(`Restart command sent for ${app.display_name}`);
      setTimeout(() => loadApps(true), 3000);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || `Failed to restart ${app.display_name}`);
    } finally {
      setRestarting(null);
    }
  };

  const handleLogs = async (app: AppInfo) => {
    setLogSearch('');
    try {
      const res = await serverAPI.getAppLogs(serverId, app.name);
      setLogsModal({ open: true, app: app.display_name, content: res.data?.logs || '(no logs)' });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to fetch logs');
    }
  };

  const handleConfig = async (app: AppInfo) => {
    try {
      const res = await serverAPI.getAppConfig(serverId, app.name);
      setConfigModal({
        open: true,
        app: app.name,
        content: res.data?.content || '',
        configFile: res.data?.config_file,
        type: res.data?.type,
        phpConfig: res.data?.config,
      });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to fetch config');
    }
  };

  const handleSaveConfig = async () => {
    if (!configModal) return;
    setConfigSaving(true);
    try {
      await serverAPI.setAppConfig(serverId, configModal.app, configModal.content, configModal.configFile);
      toast.success('Config saved successfully');
      setConfigModal(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to save config');
    } finally {
      setConfigSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
        <div className="spinner-border spinner-border-sm me-2 text-primary" />
        Detecting applications...
      </div>
    );
  }

  const runningApps = apps.filter(a => a.status === 'running');

  const filteredLogLines = logsModal?.content
    .split('\n')
    .filter(line => !logSearch || line.toLowerCase().includes(logSearch.toLowerCase()))
    ?? [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
          Applications ({runningApps.length} running · {apps.length} installed)
        </h3>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {refreshing && <span style={{ fontSize: '12px', color: '#6b7280' }}>Refreshing...</span>}
          <button
            onClick={() => loadApps(false)}
            style={{ padding: '6px 14px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
          >
            Refresh
          </button>
        </div>
      </div>

      {apps.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af' }}>
          <i className="bi bi-app display-6 d-block mb-2 opacity-50"></i>
          No applications detected on this server.
          {!wsConnected && <div style={{ fontSize: '13px', marginTop: '8px' }}>Connect the agent for real-time detection.</div>}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
          {apps.map(app => (
            <div key={app.name} style={{
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              padding: '16px',
              position: 'relative',
            }}>
              {/* Status dot */}
              <div style={{ position: 'absolute', top: '14px', right: '14px', width: '10px', height: '10px', borderRadius: '50%', background: statusColor(app.status) }} title={app.status} />

              <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '2px', paddingRight: '20px' }}>{app.display_name}</div>
              {app.version && <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>{app.version}</div>}

              {(app.memory_mb != null || app.cpu_percent != null || app.uptime) && (
                <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: '#374151', marginBottom: '12px' }}>
                  {app.memory_mb != null && app.memory_mb > 0 && <span>RAM: {app.memory_mb.toFixed(0)} MB</span>}
                  {app.cpu_percent != null && app.cpu_percent > 0 && <span>CPU: {app.cpu_percent.toFixed(1)}%</span>}
                  {app.uptime && <span>Up: {app.uptime}</span>}
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => handleRestart(app)}
                  disabled={restarting === app.name}
                  style={{ padding: '5px 12px', fontSize: '12px', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '5px', cursor: 'pointer', color: '#92400e' }}
                >
                  {restarting === app.name ? 'Restarting...' : 'Restart'}
                </button>
                <button
                  onClick={() => handleLogs(app)}
                  style={{ padding: '5px 12px', fontSize: '12px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '5px', cursor: 'pointer' }}
                >
                  Logs
                </button>
                {/* Show Config only for apps with config files (not PostgreSQL — too complex) */}
                {app.config_files && app.config_files.length > 0 && app.name !== 'postgresql' && (
                  <button
                    onClick={() => handleConfig(app)}
                    style={{ padding: '5px 12px', fontSize: '12px', background: '#ede9fe', border: '1px solid #8b5cf6', borderRadius: '5px', cursor: 'pointer', color: '#5b21b6' }}
                  >
                    Config
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Log Viewer Modal (fullscreen with search) ─────────────── */}
      {logsModal?.open && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9000, display: 'flex', flexDirection: 'column' }}
          onClick={e => { if (e.target === e.currentTarget) setLogsModal(null); }}
        >
          <div style={{ margin: '32px auto', width: '90vw', maxWidth: '1100px', height: 'calc(100vh - 64px)', background: '#1e1e2e', borderRadius: '12px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
            {/* Header */}
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #2d2d3f', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
              <span style={{ color: '#f9fafb', fontWeight: 600, fontSize: '15px' }}>{logsModal.app} — Logs</span>
              {/* Search */}
              <div style={{ flex: 1, position: 'relative' }}>
                <i className="bi bi-search" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: '13px' }}></i>
                <input
                  type="text"
                  placeholder="Search logs..."
                  value={logSearch}
                  onChange={e => setLogSearch(e.target.value)}
                  style={{ width: '100%', padding: '6px 10px 6px 32px', background: '#2d2d3f', border: '1px solid #374151', borderRadius: '6px', color: '#e5e7eb', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                  autoFocus
                />
              </div>
              <span style={{ color: '#6b7280', fontSize: '12px', whiteSpace: 'nowrap' }}>
                {filteredLogLines.length} / {logsModal.content.split('\n').length} lines
              </span>
              <button
                onClick={() => { navigator.clipboard.writeText(logsModal.content); toast.success('Copied!'); }}
                style={{ padding: '5px 12px', background: '#2d2d3f', border: '1px solid #374151', borderRadius: '6px', color: '#9ca3af', cursor: 'pointer', fontSize: '12px' }}
              >
                <i className="bi bi-clipboard me-1"></i>Copy
              </button>
              <button onClick={() => setLogsModal(null)} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '20px', lineHeight: 1 }}>×</button>
            </div>

            {/* Log content */}
            <div style={{ flex: 1, overflow: 'auto', display: 'flex' }}>
              {/* Line numbers */}
              <div style={{ padding: '12px 8px', background: '#16161e', color: '#444', fontSize: '12px', fontFamily: 'monospace', lineHeight: '1.6', userSelect: 'none', textAlign: 'right', minWidth: '48px', flexShrink: 0, borderRight: '1px solid #2d2d3f' }}>
                {filteredLogLines.map((_, i) => (
                  <div key={i}>{i + 1}</div>
                ))}
              </div>
              {/* Lines */}
              <pre style={{ flex: 1, padding: '12px 16px', margin: 0, color: '#d1d5db', fontSize: '12px', fontFamily: 'monospace', lineHeight: '1.6', whiteSpace: 'pre-wrap', wordBreak: 'break-all', overflow: 'auto' }}>
                {filteredLogLines.map((line, i) => {
                  if (!logSearch) return <div key={i}>{line || '\u00a0'}</div>;
                  const idx = line.toLowerCase().indexOf(logSearch.toLowerCase());
                  if (idx === -1) return <div key={i}>{line || '\u00a0'}</div>;
                  return (
                    <div key={i}>
                      {line.slice(0, idx)}
                      <mark style={{ background: '#fbbf24', color: '#1a1a2e' }}>{line.slice(idx, idx + logSearch.length)}</mark>
                      {line.slice(idx + logSearch.length)}
                    </div>
                  );
                })}
                {filteredLogLines.length === 0 && logSearch && (
                  <div style={{ color: '#6b7280', textAlign: 'center', paddingTop: '32px' }}>No lines match "{logSearch}"</div>
                )}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Config Modal */}
      {configModal?.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '10px', width: '80vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ fontWeight: 600 }}>{configModal.app} Config</span>
                {configModal.configFile && <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>{configModal.configFile}</span>}
              </div>
              <button onClick={() => setConfigModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#6b7280' }}>×</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
              {configModal.type === 'php_ini' && configModal.phpConfig ? (
                <PHPConfigForm
                  config={configModal.phpConfig}
                  onChange={(k, v) => setConfigModal(prev => prev ? { ...prev, phpConfig: { ...prev.phpConfig!, [k]: v } } : prev)}
                />
              ) : (
                <textarea
                  value={configModal.content}
                  onChange={e => setConfigModal(prev => prev ? { ...prev, content: e.target.value } : prev)}
                  style={{ width: '100%', height: '400px', fontFamily: 'monospace', fontSize: '13px', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '12px', boxSizing: 'border-box', resize: 'vertical' }}
                />
              )}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setConfigModal(null)} style={{ padding: '7px 16px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', background: '#f9fafb' }}>Cancel</button>
              <button onClick={handleSaveConfig} disabled={configSaving} style={{ padding: '7px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                {configSaving ? 'Saving...' : 'Save Config'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PHP_KEYS = [
  { key: 'memory_limit',        label: 'Memory Limit',       placeholder: '128M' },
  { key: 'max_execution_time',  label: 'Max Execution Time', placeholder: '30' },
  { key: 'upload_max_filesize', label: 'Upload Max Filesize', placeholder: '2M' },
  { key: 'post_max_size',       label: 'POST Max Size',      placeholder: '8M' },
  { key: 'max_input_vars',      label: 'Max Input Vars',     placeholder: '1000' },
  { key: 'display_errors',      label: 'Display Errors',     placeholder: 'Off' },
  { key: 'date.timezone',       label: 'Timezone',           placeholder: 'UTC' },
];

const PHPConfigForm: React.FC<{ config: Record<string, string>; onChange: (k: string, v: string) => void }> = ({ config, onChange }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
    {PHP_KEYS.map(({ key, label, placeholder }) => (
      <div key={key}>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px', color: '#374151' }}>{label}</label>
        <input
          value={config[key] || ''}
          onChange={e => onChange(key, e.target.value)}
          placeholder={placeholder}
          style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
        />
      </div>
    ))}
  </div>
);

export default AppStatusPanel;
